import { serverSideTranslations } from "next-i18next/serverSideTranslations";

import AnlassLayout from "@/components/anlass/AnlassLayout";
import FirmenfeierInhalt from "@/components/anlass/FirmenfeierInhalt";
import { loadCatalog } from "@/lib/catalog";
import { buildAnlassBoxen } from "@/utils/buildPopularBoxes";
import { anlassAnalyse, ohneBoxen } from "@/utils/anlassAnalyse";
import { firmenfeierFaq } from "@/utils/anlassFaq";
import { euro } from "@/utils/preisAnalyse";

/**
 * Firmenfeier: 4.795 Impressionen in 90 Tagen auf Position 14,2 — und
 * genau ein Klick.
 *
 * Größter Unterbegriff ist die Weihnachtsfeier. Zwei Themen kommen hier vor,
 * die es privat nicht gibt: das Logo im Drucklayout und die Frage, ob
 * Mitarbeiterfotos weiterverwendet werden dürfen. Beides steht auf keiner
 * Anbieterseite.
 */

const GAESTE = 100;

export async function getStaticProps({ locale }) {
  const json = await loadCatalog();
  const boxen = json.Photobooths || [];

  const analyse = anlassAnalyse(boxen, { gaeste: GAESTE, druckNoetig: true });
  const popularBoxes = buildAnlassBoxen(analyse?.boxen || [], 6);

  const stand = new Date().toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return {
    props: {
      analyse: ohneBoxen(analyse),
      popularBoxes,
      stand,
      ...(await serverSideTranslations(locale, ["common"])),
    },
// Frist von einer Stunde auf einen Tag (Absprache 2026-10-03):
// Jede Neuerzeugung schreibt die Seite in Vercels Zwischenspeicher, und
// abgerechnet wird in Blöcken zu 8 KB. Eine Seite von ~300 KB kostet so
// rund 36 Einheiten — pro Erzeugung. Stündlich erneuert reicht eine
// Handvoll gecrawlter Seiten, um das Monatskontingent von 200.000
// aufzubrauchen; am 2026-10-03 waren 75 % verbraucht, und bei 100 %
// schaltet Vercel das Projekt ab.
//
// Inhaltlich kostet es nichts: Was sich hier ändert, hängt am Tagesstand
// (daySeed, stand) und wechselt ohnehin nur einmal am Tag.
    revalidate: 86400,
  };
}

export default function FirmenfeierSeite({ analyse, popularBoxes, stand }) {
  const abPreis = analyse?.min || 0;

  return (
    <AnlassLayout
      seo={{
        titel: abPreis
          ? `Fotobox für die Firmenfeier mieten ab ${euro(abPreis)} € – im Vergleich`
          : "Fotobox für die Firmenfeier mieten – Preise & Anbieter",
        beschreibung: analyse
          ? `Fotobox für Firmenfeier und Weihnachtsfeier mieten: Pakete für ${analyse.gaeste} Gäste ab ${euro(analyse.min)} €, mit Logo im Drucklayout und Rechnung auf die Firma.`
          : "Fotobox für die Firmenfeier mieten: Preise, Pakete und Anbieter im Vergleich.",
        pfad: "/fotobox-mieten-firmenfeier",
      }}
      breadcrumb="Fotobox für die Firmenfeier"
      hero={{
        titel: "Fotobox für die Firmenfeier mieten",
        lead: "Für Weihnachtsfeier, Sommerfest oder Messe — mit Logo auf den Ausdrucken.",
        anzahlBoxen: analyse?.anzahlBoxen || 0,
        anzahlVersand: analyse?.perVersand || 0,
        anzahlRegional: analyse?.bauenAuf || 0,
        abPreis,
        hinweis: (
          <>
            {abPreis > 0 && (
              <>
                Ab{" "}
                <strong className="font-semibold text-white">
                  {euro(abPreis)} €
                </strong>
                .{" "}
              </>
            )}
            Rechnung auf die Firma, eigenes Drucklayout mit Logo möglich.
          </>
        ),
      }}
      angebote={{
        titel: "Fotoboxen für Firmenfeier und Weihnachtsfeier",
        untertitel: analyse
          ? `Ausgewählt aus den Angeboten, die für ${analyse.gaeste} Gäste genug Ausdrucke liefern.`
          : "Ausgewählt nach Druckleistung und Ausstattung.",
        items: popularBoxes,
      }}
      faq={firmenfeierFaq(analyse)}
      faqTitel="Häufige Fragen zur Fotobox auf der Firmenfeier"
      faqUntertitel="Was Organisatorinnen und Organisatoren am häufigsten fragen."
      staedteTitel="Fotobox für die Firmenfeier mieten in deiner Stadt"
      abschlussTitel="Jetzt die passende Fotobox für die Firmenfeier finden"
    >
      <FirmenfeierInhalt analyse={analyse} stand={stand} />
    </AnlassLayout>
  );
}
